import { memo } from 'react'
import Link from 'next/link'
import { noop } from 'lodash'

import { useListingCardData } from '@/components/listing-card-new/hooks/useListingCardData'
import { ListingCard } from '@/components/listing-card-new/ListingCard'
import { ListingCardProps } from '@/components/listing-card-new/ListingCard.types'
import { ListingCardHorizontal } from '@/components/listing-card-new/listingCardHorizontal'
import { Listing } from '@/components/listing-card-new/types'

interface ListingCardItemProps {
  id: string
  listing: Listing
  isFeatured?: boolean
  hasSession: boolean
  isAgreementMismatched: boolean
  isAgreementLoading: boolean
  suppressUnitAvailable?: boolean
  horizontal?: boolean
  onClick: () => void
  onClose?: () => void
  onListingHover: () => void
  messageClickId?: string
  trackImpression?: {
    id: string[] | string
    enabled: boolean
    page: string
    section: string
    sponsored: boolean
  }
}

const ListingCardItem = memo(
  ({
    id,
    listing,
    isFeatured = false,
    hasSession = false,
    isAgreementMismatched = false,
    isAgreementLoading = false,
    horizontal = false,
    suppressUnitAvailable = false,
    onClick = noop,
    onClose,
    onListingHover = () => {},
    messageClickId,
    trackImpression
  }: ListingCardItemProps) => {
    const listingData = useListingCardData(listing, {
      isFeatured,
      hasSession,
      isAgreementMismatched,
      isAgreementLoading
    })

    if (!listingData) return null

    const key = isFeatured ? `featured-${id}` : `${id}`

    const baseProps = {
      id,
      ...listingData,
      onClick: listingData.variant !== 'locked' ? onClick : noop,
      onClose,
      specialOffer: listingData.specialOffer,
      onHover: onListingHover,
      listing,
      suppressUnitAvailable,
      displayPrice: listingData.displayPrice,
      displayDetails: listingData.displayDetails,
      displayAddress: listingData.displayAddress,
      displayCityRegion: listingData.displayCityRegion,
      isSingleListing: listingData.isSingleListing,
      messageClickId,
      trackImpression: {
        id: trackImpression?.id,
        enabled: trackImpression?.enabled,
        page: trackImpression?.page,
        section: trackImpression?.section,
        sponsored: trackImpression?.sponsored
      }
    }

    const finalProps = {
      ...baseProps
    } as ListingCardProps

    const ListingCardComponent = horizontal
      ? ListingCardHorizontal
      : ListingCard
    const listingCard = <ListingCardComponent {...finalProps} key={key} />

    if (!listingData.shouldWrapWithExternalLink || !finalProps.listingLink) {
      return listingCard
    }

    return (
      <Link href={finalProps.listingLink} legacyBehavior passHref>
        <a target="_blank" rel="noopener" style={{ textDecoration: 'none' }}>
          {listingCard}
        </a>
      </Link>
    )
  }
)

ListingCardItem.displayName = 'ListingCardItem'

export { ListingCardItem }
